import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Plus, 
  Edit, 
  Trash2,
  Save,
  X,
  ArrowLeft,
  HelpCircle,
  Search,
  Filter
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import SimpleWYSIWYG from '../../components/SimpleWYSIWYG';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility function to clean HTML content
const cleanHtmlContent = (html) => {
  if (!html) return '';
  
  // Remove empty paragraphs and clean whitespace
  let cleaned = html
    .replace(/<p><br><\/p>/g, '')
    .replace(/<p>&nbsp;<\/p>/g, '')
    .replace(/<p>\s*<\/p>/g, '')
    .trim();
  
  // If only empty tags remain, return empty string
  if (cleaned === '<p></p>' || cleaned === '') {
    return '';
  }
  
  return cleaned;
};

// Utility function to strip HTML tags for preview
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};

const FAQ = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: ''
  });
  
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'Général',
    display_order: 0,
    is_active: true
  });

  const categories = [
    'Général',
    'Services Sportifs',
    'Tarifs & Facturation', 
    'Équipement & Technique',
    'Délais & Livraison',
    'Zone d\'intervention'
  ];

  useEffect(() => {
    loadFaqs();
  }, [filters]);

  const loadFaqs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      
      const response = await axios.get(`${API}/admin/faqs?${params}`);
      
      if (response.data && response.data.success) {
        let faqList = response.data.data;
        
        // Client-side search filtering
        if (filters.search) {
          faqList = faqList.filter(faq => 
            faq.question.toLowerCase().includes(filters.search.toLowerCase()) ||
            faq.answer.toLowerCase().includes(filters.search.toLowerCase())
          );
        }
        
        setFaqs(faqList);
      }
    } catch (error) {
      console.error('Failed to load FAQs:', error);
      if (error.response?.status === 401) {
        toast({
          title: "Session expirée",
          description: "Veuillez vous reconnecter",
          variant: "destructive",
        });
        // Redirect to login
        window.location.href = '/admin/login';
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de charger les FAQ",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation du contenu avec fonction utilitaire
    const cleanAnswer = cleanHtmlContent(formData.answer);
    if (!cleanAnswer) {
      toast({
        title: "Erreur",
        description: "La réponse ne peut pas être vide",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const dataToSend = {
        ...formData,
        answer: cleanAnswer
      };
      
      if (editingFaq) {
        await axios.put(`${API}/admin/faqs/${editingFaq.id}`, dataToSend);
        toast({
          title: "Succès",
          description: "FAQ modifiée avec succès",
        });
      } else {
        await axios.post(`${API}/admin/faqs`, dataToSend);
        toast({
          title: "Succès",
          description: "FAQ ajoutée avec succès",
        });
      }
      
      resetForm();
      loadFaqs();
    } catch (error) {
      console.error('Failed to save FAQ:', error);
      
      if (error.response?.status === 401) {
        toast({
          title: "Session expirée",
          description: "Veuillez vous reconnecter",
          variant: "destructive",
        });
        window.location.href = '/admin/login';
      } else {
        toast({
          title: "Erreur",
          description: error.response?.data?.detail || error.message || "Erreur lors de l'enregistrement",
          variant: "destructive",
        });
      }
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      display_order: faq.display_order,
      is_active: faq.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette FAQ ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/faqs/${faqId}`);
      toast({
        title: "Succès",
        description: "FAQ supprimée avec succès",
      });
      loadFaqs();
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive",
      });
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      category: 'Général',
      display_order: 0,
      is_active: true
    });
    setEditingFaq(null);
    setShowForm(false);
  };

  if (showForm) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <button
              onClick={resetForm}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour à la liste
            </button>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingFaq ? 'Modifier la FAQ' : 'Ajouter une FAQ'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.question}
                    onChange={(e) => setFormData({...formData, question: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Quels sont vos tarifs pour un événement sportif ?"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Réponse *
                  </label>
                  <SimpleWYSIWYG
                    value={formData.answer}
                    onChange={(content) => setFormData({...formData, answer: content})}
                    placeholder="Réponse détaillée à la question (utilisez la barre d'outils pour formater)..."
                    minHeight="250px"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Utilisez la barre d'outils pour formater votre réponse (gras, listes, liens, etc.)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ordre d'affichage
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">FAQ active (visible sur le site)</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors"
                  style={{ backgroundColor: '#f97316' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingFaq ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4">
            <button
              onClick={() => window.location.href = '/admin'}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au tableau de bord
            </button>
          </div>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Gestion des FAQ</h1>
              <p className="text-gray-600">Gérez les questions fréquemment posées</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors"
              style={{ backgroundColor: '#f97316' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter FAQ
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rechercher
              </label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Question ou réponse..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catégorie
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Toutes les catégories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* FAQs List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: '#f97316' }}></div>
              <p className="text-gray-600">Chargement des FAQ...</p>
            </div>
          ) : (
            <>
              {faqs.length === 0 ? (
                <div className="p-8 text-center">
                  <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune FAQ</h3>
                  <p className="text-gray-600 mb-4">Commencez par créer votre première FAQ</p>
                  <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors"
                    style={{ backgroundColor: '#f97316' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer une FAQ
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {faqs.map((faq) => (
                    <div key={faq.id} className="p-6 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 mr-2">
                              {faq.category}
                            </span>
                            {!faq.is_active && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Inactive
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {faq.question}
                          </h3>
                          <p className="text-gray-600 mb-2 line-clamp-3">
                            {stripHtml(faq.answer).substring(0, 150)}{stripHtml(faq.answer).length > 150 ? '...' : ''}
                          </p>
                          <div className="text-sm text-gray-500">
                            Ordre: {faq.display_order} • 
                            Créé le {new Date(faq.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleEdit(faq)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(faq.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQ;